import { supabase } from "./supabase";

export const productCategoryIds = [
  "groceries",
  "household",
  "personal",
  "baby",
  "telecom",
  "transport",
  "dining",
  "clothing",
  "catalogue",
] as const;

export type ProductCategoryId = typeof productCategoryIds[number];

export type CatalogProduct = {
  name: string;
  category: ProductCategoryId;
  imageUrl?: string;
};

export type CatalogBrand = {
  name: string;
  imageUrl: string;
};

export type CatalogProductBrandLink = {
  productName: string;
  brandName: string;
  brandDetail: string;
  sourceUrl: string;
  imageUrl: string;
};

type CatalogProductRow = {
  product_key: string;
  product_name: string;
  category_id: string;
  image_url: string | null;
};

type CatalogBrandRow = {
  brand_key: string;
  brand_name: string;
};

type CatalogProductBrandLinkRow = {
  product_name: string;
  brand_name: string;
  brand_detail: string;
  source_url: string | null;
  image_url: string | null;
};

type CatalogProductBrandOptionRow = {
  product_name: string;
  brand_name: string;
};

export type CatalogProductBrandOption = {
  productName: string;
  brandName: string;
};

type CatalogDisplayImageRow = {
  entity_type: "product" | "brand";
  entity_name: string;
  image_url: string;
};

export const productCategoryLabels: Record<ProductCategoryId, string> = {
  groceries: "Groceries",
  household: "Home care",
  personal: "Personal care",
  baby: "Baby products",
  telecom: "Mobile & data",
  transport: "Fuel & transport",
  dining: "Food & dining",
  clothing: "Clothing",
  catalogue: "Product catalogue",
};

export const fallbackProductsByCategory: Record<ProductCategoryId, string[]> = {
  groceries: ["Cooking oil", "Rice", "Fresh milk", "Eggs", "Coffee", "Instant noodles"],
  household: ["Laundry detergent", "Dishwashing liquid", "Tissue", "Floor cleaner"],
  personal: ["Shampoo", "Body wash", "Toothpaste", "Skincare"],
  baby: ["Diapers", "Baby formula", "Baby wipes", "Baby toiletries"],
  telecom: ["Mobile data", "Home internet", "Prepaid top-up"],
  transport: ["Fuel", "E-hailing", "Public transport pass"],
  dining: ["Family meals", "Lunch deals", "Coffee & drinks"],
  clothing: ["School uniforms", "Workwear", "Everyday basics"],
  catalogue: [],
};

const productCategoryAliases = new Map<string, ProductCategoryId>([
  ...productCategoryIds.map((id) => [id, id] as const),
  ...Object.entries(productCategoryLabels).map(([id, label]) => [label.toLocaleLowerCase(), id as ProductCategoryId] as const),
  ["homecare", "household"],
  ["personalcare", "personal"],
  ["babyproducts", "baby"],
  ["mobiledata", "telecom"],
  ["fueltransport", "transport"],
  ["fooddining", "dining"],
]);

function normalizeKey(value: string) {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

export function normalizeCatalogName(value: string, maxLength = 80) {
  return value.trim().replace(/\s+/g, " ").slice(0, maxLength);
}

export function normalizeProductCategory(value: string): ProductCategoryId | null {
  const normalized = value.trim().toLocaleLowerCase();
  return productCategoryAliases.get(normalized)
    ?? productCategoryAliases.get(normalized.replace(/[^a-z0-9]/g, ""))
    ?? null;
}

export async function loadCatalogProducts(): Promise<CatalogProduct[]> {
  const [{ data, error }, displayImages] = await Promise.all([
    supabase.from("catalog_products").select("product_key,product_name,category_id,image_url").eq("active", true).order("product_name"),
    loadCatalogDisplayImages(),
  ]);
  if (error) throw error;
  const productImages = new Map(displayImages.filter((row) => row.entity_type === "product").map((row) => [normalizeKey(row.entity_name), row.image_url]));
  return ((data ?? []) as CatalogProductRow[]).flatMap((row) => {
    const category = normalizeProductCategory(row.category_id);
    return category ? [{ name: row.product_name, category, imageUrl: productImages.get(normalizeKey(row.product_name)) ?? row.image_url ?? "" }] : [];
  });
}

export async function loadCatalogBrands(): Promise<CatalogBrand[]> {
  const [{ data, error }, displayImages] = await Promise.all([
    supabase.from("catalog_brands").select("brand_key,brand_name").eq("active", true).order("brand_name"),
    loadCatalogDisplayImages(),
  ]);
  if (error) throw error;
  const brandImages = new Map(displayImages.filter((row) => row.entity_type === "brand").map((row) => [normalizeKey(row.entity_name), row.image_url]));
  return ((data ?? []) as CatalogBrandRow[]).map((row) => ({ name: row.brand_name, imageUrl: brandImages.get(normalizeKey(row.brand_name)) ?? "" }));
}

async function loadCatalogDisplayImages(): Promise<CatalogDisplayImageRow[]> {
  const { data, error } = await supabase.rpc("get_catalog_display_images");
  if (error) return [];
  return (data ?? []) as CatalogDisplayImageRow[];
}

export async function loadCatalogProductBrandLinks(): Promise<CatalogProductBrandLink[]> {
  const { data, error } = await supabase
    .from("catalog_product_brand_links")
    .select("product_name,brand_name,brand_detail,source_url,image_url")
    .eq("active", true)
    .order("product_name")
    .order("brand_name")
    .order("brand_detail");
  if (error) throw error;
  return ((data ?? []) as CatalogProductBrandLinkRow[]).map((row) => ({
    productName: row.product_name,
    brandName: row.brand_name,
    brandDetail: row.brand_detail,
    sourceUrl: row.source_url ?? "",
    imageUrl: row.image_url ?? "",
  }));
}

export async function loadCatalogProductBrandOptions(): Promise<CatalogProductBrandOption[]> {
  const { data, error } = await supabase.rpc("get_catalog_product_brand_options");
  if (error) throw error;
  return ((data ?? []) as CatalogProductBrandOptionRow[]).map((row) => ({
    productName: row.product_name,
    brandName: row.brand_name,
  }));
}

export async function loadCatalogBrandsForProducts(productNames: string[]): Promise<CatalogBrand[] | null> {
  const requested = new Set(productNames.map(normalizeKey).filter(Boolean));
  if (!requested.size) return null;
  const [{ data, error }, displayImages] = await Promise.all([
    supabase.rpc("get_catalog_product_brand_options"),
    loadCatalogDisplayImages(),
  ]);
  if (error) return null;
  const brandImages = new Map(displayImages.filter((row) => row.entity_type === "brand").map((row) => [normalizeKey(row.entity_name), row.image_url]));
  const matches = ((data ?? []) as CatalogProductBrandOptionRow[])
    .filter((row) => requested.has(normalizeKey(row.product_name)))
    .map((row) => ({ name: row.brand_name, imageUrl: brandImages.get(normalizeKey(row.brand_name)) ?? "" }));
  if (!matches.length) return null;
  return Array.from(new Map(matches.map((brand) => [normalizeKey(brand.name), brand])).values());
}

export async function importCatalogProducts(products: CatalogProduct[]) {
  const unique = Array.from(new Map(products.map((product) => [normalizeKey(product.name), product])).values());
  const { error } = await supabase.from("catalog_products").upsert(unique.map((product) => ({
    product_key: normalizeKey(product.name),
    product_name: normalizeCatalogName(product.name),
    category_id: product.category,
    active: true,
    updated_at: new Date().toISOString(),
  })), { onConflict: "product_key" });
  if (error) throw error;
  return unique.length;
}

export async function importCatalogBrands(brands: CatalogBrand[]) {
  const unique = Array.from(new Map(brands.map((brand) => [normalizeKey(brand.name), brand])).values());
  const { error } = await supabase.from("catalog_brands").upsert(unique.map((brand) => ({
    brand_key: normalizeKey(brand.name),
    brand_name: normalizeCatalogName(brand.name, 60),
    active: true,
    updated_at: new Date().toISOString(),
  })), { onConflict: "brand_key" });
  if (error) throw error;
  return unique.length;
}

export async function linkCatalogBrandToProduct(brandName: string, product: CatalogProduct) {
  const cleanBrandName = normalizeCatalogName(brandName, 60);
  const cleanProductName = normalizeCatalogName(product.name);
  const brandKey = normalizeKey(cleanBrandName);
  const productKey = normalizeKey(cleanProductName);
  const now = new Date().toISOString();

  const { error: productError } = await supabase.from("catalog_products").upsert({
    product_key: productKey,
    product_name: cleanProductName,
    category_id: product.category,
    active: true,
    updated_at: now,
  }, { onConflict: "product_key" });
  if (productError) throw productError;

  const { error: brandError } = await supabase.from("catalog_brands").upsert({
    brand_key: brandKey,
    brand_name: cleanBrandName,
    active: true,
    updated_at: now,
  }, { onConflict: "brand_key" });
  if (brandError) throw brandError;

  const entryKey = `${productKey}::${brandKey}::`;
  const { error: linkError } = await supabase.from("catalog_product_brand_links").upsert({
    entry_key: entryKey,
    product_key: productKey,
    product_name: cleanProductName,
    brand_key: brandKey,
    brand_name: cleanBrandName,
    brand_detail: "",
    source_url: "",
    image_url: "",
    active: true,
    updated_at: now,
  }, { onConflict: "entry_key" });
  if (linkError) throw linkError;
}

export async function importCatalogProductBrandLinks(entries: CatalogProductBrandLink[]) {
  const uniqueEntries = Array.from(new Map(entries.map((entry) => {
    const productName = normalizeCatalogName(entry.productName);
    const brandName = normalizeCatalogName(entry.brandName, 60);
    const brandDetail = normalizeCatalogName(entry.brandDetail, 240);
    const sourceUrl = normalizeCatalogName(entry.sourceUrl ?? "", 1_000);
    const imageUrl = normalizeCatalogName(entry.imageUrl ?? "", 1_000);
    const entryKey = [normalizeKey(productName), normalizeKey(brandName), normalizeKey(brandDetail)].join("::");
    return [entryKey, { productName, brandName, brandDetail, sourceUrl, imageUrl, entryKey }];
  })).values());
  const products = Array.from(new Map(uniqueEntries.map((entry) => [normalizeKey(entry.productName), entry.productName])).entries());
  const brands = Array.from(new Map(uniqueEntries.map((entry) => [normalizeKey(entry.brandName), entry.brandName])).entries());
  const now = new Date().toISOString();

  const { error: productError } = await supabase.from("catalog_products").upsert(products.map(([productKey, productName]) => ({
    product_key: productKey,
    product_name: productName,
    category_id: "catalogue",
    active: true,
    updated_at: now,
  })), { onConflict: "product_key" });
  if (productError) throw productError;

  const { error: brandError } = await supabase.from("catalog_brands").upsert(brands.map(([brandKey, brandName]) => ({
    brand_key: brandKey,
    brand_name: brandName,
    active: true,
    updated_at: now,
  })), { onConflict: "brand_key" });
  if (brandError) throw brandError;

  const { error: linkError } = await supabase.from("catalog_product_brand_links").upsert(uniqueEntries.map((entry) => ({
    entry_key: entry.entryKey,
    product_key: normalizeKey(entry.productName),
    product_name: entry.productName,
    brand_key: normalizeKey(entry.brandName),
    brand_name: entry.brandName,
    brand_detail: entry.brandDetail,
    source_url: entry.sourceUrl,
    image_url: entry.imageUrl,
    active: true,
    updated_at: now,
  })), { onConflict: "entry_key" });
  if (linkError) throw linkError;

  const { error: reconcileError } = await supabase.rpc("admin_reconcile_catalog", {
    p_entry_keys: uniqueEntries.map((entry) => entry.entryKey),
    p_brand_keys: brands.map(([brandKey]) => brandKey),
  });
  if (reconcileError) throw reconcileError;

  return { entries: uniqueEntries.length, products: products.length, brands: brands.length };
}
